// UncertaintyMeter — 不确定性量化
// 规格: SPEC §6 ui-kit/explainability / ADR-0013 (ECharts 5.5 按需引入) / ADR-0008 (SaMD 路径 A)
//
// SaMD 路径 A 约束 (ADR-0008):
//   仅展示客观置信度与置信区间, 禁止诊断结论文案 (MED-02)

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import type { ComposeOption } from 'echarts/core';
import { GaugeChart } from 'echarts/charts';
import type { GaugeSeriesOption } from 'echarts/charts';
import { TooltipComponent, TitleComponent } from 'echarts/components';
import type {
  TooltipComponentOption,
  TitleComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// ECharts 按需注册 (ADR-0013)
echarts.use([
  GaugeChart,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer,
]);

type UncertaintyOption = ComposeOption<
  | GaugeSeriesOption
  | TooltipComponentOption
  | TitleComponentOption
>;

const MIN_VALUE = 0;
const MAX_VALUE = 100;

/** 置信区间内颜色 (蓝) */
const COLOR_CI = '#3b82f6';
/** 置信区间外颜色 (灰) */
const COLOR_OUT = '#e5e7eb';

export interface UncertaintyMeterProps {
  /** 置信度 [0, 1] */
  readonly confidence: number;
  /** 置信区间下界 [0, 1] (可选) */
  readonly lowerBound?: number;
  /** 置信区间上界 [0, 1] (可选) */
  readonly upperBound?: number;
  /** 标签 (默认 "置信度") */
  readonly label?: string;
  /** 容器高度 (默认 240px) */
  readonly height?: number | string;
  /** 自定义无障碍标签 */
  readonly ariaLabel?: string;
}

/**
 * UncertaintyMeter — 不确定性量化
 *
 * 规格:
 *   - SPEC §6 ui-kit/explainability
 *   - ADR-0013: ECharts 5.5.x 按需引入 (GaugeChart)
 *   - ADR-0008 SaMD 路径 A: 仅展示客观数据与 AI 可解释性, 禁止诊断结论文案
 *
 * 渲染:
 *   - ECharts GaugeChart 仪表盘
 *   - 指针指示置信度
 *   - 置信区间用蓝色高亮, 区间外用灰色
 *   - 中心显示百分比数值
 */
export function UncertaintyMeter({
  confidence,
  lowerBound,
  upperBound,
  label = '置信度',
  height = 240,
  ariaLabel,
}: UncertaintyMeterProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // 归一化到 [0, 100]
  const confidencePct = clamp(toPercent(confidence), MIN_VALUE, MAX_VALUE);
  const lowerPct =
    lowerBound !== undefined
      ? clamp(toPercent(lowerBound), MIN_VALUE, MAX_VALUE)
      : undefined;
  const upperPct =
    upperBound !== undefined
      ? clamp(toPercent(upperBound), MIN_VALUE, MAX_VALUE)
      : undefined;

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;
    return () => {
      // StrictMode 双挂载靠 dispose 幂等 (ADR-0013)
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // 构建仪表盘分段颜色: [percentage(0-1), color]
    // ECharts gauge axisLine.lineStyle.color 格式
    const colorStops: Array<[number, string]> = [];
    if (lowerPct !== undefined && upperPct !== undefined) {
      const lowerFrac = lowerPct / MAX_VALUE;
      const upperFrac = upperPct / MAX_VALUE;
      if (lowerFrac > 0) {
        colorStops.push([lowerFrac, COLOR_OUT]);
      }
      colorStops.push([upperFrac, COLOR_CI]);
      if (upperFrac < 1) {
        colorStops.push([1, COLOR_OUT]);
      }
    } else {
      colorStops.push([1, COLOR_OUT]);
    }

    const option: UncertaintyOption = {
      title: {
        text: label,
        left: 'center',
        textStyle: { fontSize: 14 },
      },
      tooltip: {
        formatter: () => {
          const parts = [`${label}: ${confidencePct.toFixed(1)}%`];
          if (lowerPct !== undefined && upperPct !== undefined) {
            parts.push(
              `置信区间: ${lowerPct.toFixed(1)}% - ${upperPct.toFixed(1)}%`,
            );
          }
          return parts.join('<br/>');
        },
      },
      series: [
        {
          type: 'gauge',
          min: MIN_VALUE,
          max: MAX_VALUE,
          progress: {
            show: true,
            width: 18,
          },
          axisLine: {
            lineStyle: {
              width: 18,
              color: colorStops,
            },
          },
          pointer: {
            itemStyle: {
              color: 'auto',
            },
          },
          axisTick: {
            distance: -25,
            length: 5,
            lineStyle: {
              color: '#fff',
              width: 2,
            },
          },
          splitLine: {
            distance: -20,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 4,
            },
          },
          axisLabel: {
            distance: -10,
            color: '#999',
            fontSize: 12,
            formatter: '{value}%',
          },
          anchor: {
            show: true,
            size: 20,
            itemStyle: {
              color: '#999',
            },
          },
          detail: {
            valueAnimation: true,
            width: '60%',
            lineHeight: 40,
            height: 40,
            borderRadius: 8,
            offsetCenter: [0, '70%'],
            fontSize: 24,
            fontWeight: 'bold',
            formatter: '{value}%',
            color: 'inherit',
          },
          data: [
            {
              value: Math.round(confidencePct),
              name: label,
            },
          ],
        },
      ],
    };

    chart.setOption(option, { notMerge: false });
  }, [confidencePct, lowerPct, upperPct, label]);

  const ciText =
    lowerPct !== undefined && upperPct !== undefined
      ? `置信区间 ${lowerPct.toFixed(1)}% - ${upperPct.toFixed(1)}%`
      : '无置信区间数据';

  return (
    <div
      role="img"
      aria-label={
        ariaLabel ?? `${label}: ${confidencePct.toFixed(1)}%, ${ciText}`
      }
      style={{ width: '100%', height }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {/* WCAG 2.1 SC 1.1.1: 屏幕阅读器数据表格替代 (Canvas 对 AT 不可见) */}
      <table className="sr-only">
        <caption>{label} (客观展示, 非诊断结论)</caption>
        <tbody>
          <tr>
            <th scope="row">置信度</th>
            <td>{confidencePct.toFixed(1)}%</td>
          </tr>
          {lowerPct !== undefined && (
            <tr>
              <th scope="row">置信区间下界</th>
              <td>{lowerPct.toFixed(1)}%</td>
            </tr>
          )}
          {upperPct !== undefined && (
            <tr>
              <th scope="row">置信区间上界</th>
              <td>{upperPct.toFixed(1)}%</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** 将 [0, 1] 转为 [0, 100] */
function toPercent(v: number): number {
  return v * 100;
}

/** 限制到 [min, max] */
function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
